import { Box, Typography } from "@mui/material";

import styles from "./styles";

interface StatCardData {
  icon: string;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string;
  valueColor: string;
  caption: string;
  captionIcon?: string;
  highlight?: boolean;
  onClick?: () => void;
}

export interface BestStat {
  value: number;
  templateName?: string;
}

interface StatsBarProps {
  totalPitchesSent: number | null;
  outreachTemplateCount: number;
  followupTemplateCount: number;
  bestOpenRate: BestStat | null;
  bestReplyRate: BestStat | null;
  templatesEvaluated: number;
  totalTemplates: number;
  onEvaluatedClick: () => void;
}

const formatPercentage = (value: number) => `${Math.round(value * 100)}%`;

const NO_STAT = "No stat yet.";

const buildStats = ({
  totalPitchesSent,
  outreachTemplateCount,
  followupTemplateCount,
  bestOpenRate,
  bestReplyRate,
  templatesEvaluated,
  totalTemplates,
  onEvaluatedClick,
}: StatsBarProps): StatCardData[] => {
  const stats: StatCardData[] = [
    {
      icon: "fa-regular fa-paper-plane",
      iconBg: "#ececf0",
      iconColor: "#717182",
      label: "Total pitches sent",
      value:
        totalPitchesSent === null ? "—" : totalPitchesSent.toLocaleString(),
      valueColor: "#0a0a0a",
      caption: "All templates combined",
    },
    {
      icon: "fa-regular fa-file-lines",
      iconBg: "#ececf0",
      iconColor: "#717182",
      label: "Templates created",
      value: (outreachTemplateCount + followupTemplateCount).toLocaleString(),
      valueColor: "#0a0a0a",
      caption: `${outreachTemplateCount} outreach · ${followupTemplateCount} follow-up`,
    },
    {
      icon: "fa-solid fa-arrow-trend-up",
      iconBg: "#030213",
      iconColor: "white",
      label: "Best ever open rate",
      value: bestOpenRate ? formatPercentage(bestOpenRate.value) : "—",
      valueColor: bestOpenRate ? "#00a63e" : "#717182",
      caption: bestOpenRate?.templateName ?? NO_STAT,
    },
    {
      icon: "fa-regular fa-comment-dots",
      iconBg: "#030213",
      iconColor: "white",
      label: "Best ever reply rate",
      value: bestReplyRate ? formatPercentage(bestReplyRate.value) : "—",
      valueColor: bestReplyRate ? "#00a63e" : "#717182",
      caption: bestReplyRate?.templateName ?? NO_STAT,
    },
  ];

  const notEvaluated = totalTemplates - templatesEvaluated;
  if (notEvaluated > 0) {
    stats.push({
      icon: "fa-solid fa-wand-magic-sparkles",
      iconBg: "#fe9a00",
      iconColor: "white",
      label: "Templates evaluated",
      value: `${templatesEvaluated} / ${totalTemplates}`,
      valueColor: "#bb4d00",
      caption: `Score ${notEvaluated} more`,
      captionIcon: "fa-solid fa-arrow-right",
      highlight: true,
      onClick: onEvaluatedClick,
    });
  }

  return stats;
};

export default function StatsBar(props: StatsBarProps) {
  const stats = buildStats(props);
  return (
    <Box sx={styles.statsBar(stats.length)}>
      {stats.map((stat) => (
        <Box
          key={stat.label}
          onClick={stat.onClick}
          sx={[
            styles.statCard(Boolean(stat.highlight)),
            ...(stat.onClick ? [styles.statCardClickable] : []),
          ]}
        >
          <Box sx={styles.statIcon(stat.iconBg)}>
            <Box
              component="i"
              className={stat.icon}
              sx={{ fontSize: 14, color: stat.iconColor }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={styles.statLabelTop(Boolean(stat.highlight))}>
              {stat.label}
            </Typography>
            <Typography sx={styles.statValue(stat.valueColor)}>
              {stat.value}
            </Typography>
            <Typography sx={styles.statCaption(Boolean(stat.highlight))}>
              {stat.caption}
              {stat.captionIcon && (
                <Box
                  component="i"
                  className={stat.captionIcon}
                  sx={{ fontSize: 10 }}
                />
              )}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
